import { useMutation, useSuspenseQuery } from '@apollo/client';
import { skipToken } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Badge,
  DateTimePicker,
  Divider,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Form,
  FormField,
  FormItem,
  FormMessage,
  HStack,
  Input,
  InputLabel,
  Label,
  NewButton,
  PageTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@eluve/components';
import { formatDate } from '@eluve/date-utils';

import { DeleteExperimentAction } from './DeleteExperimentAction';
import { ExperimentStatusToggle } from './ExperimentStatusToggle';
import { ExperimentVariantsList } from './ExperimentVariantsList';
import { TestAssignmentSection } from './TestAssignmentSection';
import { TestingOverridesSection } from './TestingOverridesSection';
import {
  createExperimentMutation,
  getExperimentByIdQuery,
  getExperimentsQuery,
  updateExperimentMutation,
} from './operations';

const TIMEZONES = [
  { value: 'Pacific/Honolulu', label: 'UTC-10:00 Hawaii' },
  { value: 'America/Anchorage', label: 'UTC-09:00 Alaska' },
  { value: 'America/Los_Angeles', label: 'UTC-08:00 Pacific Time (US)' },
  { value: 'America/Denver', label: 'UTC-07:00 Mountain Time (US)' },
  { value: 'America/Chicago', label: 'UTC-06:00 Central Time (US)' },
  { value: 'America/New_York', label: 'UTC-05:00 Eastern Time (US)' },
  { value: 'America/Halifax', label: 'UTC-04:00 Atlantic Time' },
  { value: 'America/Sao_Paulo', label: 'UTC-03:00 São Paulo' },
  { value: 'Atlantic/South_Georgia', label: 'UTC-02:00 Mid-Atlantic' },
  { value: 'Atlantic/Azores', label: 'UTC-01:00 Azores' },
  { value: 'UTC', label: 'UTC+00:00 UTC' },
  { value: 'Europe/London', label: 'UTC+00:00 London' },
  { value: 'Europe/Paris', label: 'UTC+01:00 Paris, Berlin' },
  { value: 'Europe/Helsinki', label: 'UTC+02:00 Helsinki, Cairo' },
  { value: 'Europe/Moscow', label: 'UTC+03:00 Moscow' },
  { value: 'Asia/Dubai', label: 'UTC+04:00 Dubai' },
  { value: 'Asia/Karachi', label: 'UTC+05:00 Karachi' },
  { value: 'Asia/Dhaka', label: 'UTC+06:00 Dhaka' },
  { value: 'Asia/Bangkok', label: 'UTC+07:00 Bangkok' },
  { value: 'Asia/Jakarta', label: 'UTC+07:00 Jakarta' },
  { value: 'Asia/Singapore', label: 'UTC+08:00 Singapore, Perth' },
  { value: 'Asia/Tokyo', label: 'UTC+09:00 Tokyo, Seoul' },
  { value: 'Australia/Sydney', label: 'UTC+10:00 Sydney, Melbourne' },
  { value: 'Pacific/Auckland', label: 'UTC+12:00 Auckland' },
] as const;

const experimentFormSchema = z.object({
  name: z.string().min(1, 'Experiment name is required'),
  description: z.string().optional(),
  posthogFlagKey: z.string().min(1, 'PostHog flag key is required'),
  timezone: z.string().optional(),
  startTime: z.date().optional(),
  endTime: z.date().optional(),
  rank: z.coerce.number().int().default(0),
});

type ExperimentFormValues = z.infer<typeof experimentFormSchema>;

type TestingOverride = {
  userId: string;
  variantKey: string;
};

type ExperimentData = {
  id: string;
  name: string;
  description: string | null;
  posthogFlagKey: string;
  timezone: string | null;
  startTime: string | null;
  endTime: string | null;
  isActive: boolean;
  firstActivatedAt: string | null;
  rank: number;
  testingOverrides: TestingOverride[] | null;
  createdAt: string;
  updatedAt: string;
  variants: Array<{
    id: string;
    experimentId: string;
    name: string;
    posthogVariantKey: string;
    variantConfig: Record<string, unknown>;
    createdAt: string;
    updatedAt: string;
  }>;
};

export const ExperimentPage: React.FC = () => {
  const { experimentId } = useParams<{ experimentId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const apiClient = useApiClient();

  const isCreateMode = experimentId === 'new';

  const [flagValidation, setFlagValidation] = useState<{
    isValidating: boolean;
    exists: boolean | null;
  }>({ isValidating: false, exists: null });

  const { data } = useSuspenseQuery(
    getExperimentByIdQuery,
    isCreateMode || !experimentId ? skipToken : { variables: { experimentId } },
  );

  const experiment = data?.experimentsByPk as ExperimentData | null;
  const isActivated = experiment?.firstActivatedAt != null;

  const parseDate = (dateStr: string | null | undefined): Date | undefined => {
    if (!dateStr) return undefined;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? undefined : date;
  };

  const form = useForm<ExperimentFormValues>({
    resolver: zodResolver(experimentFormSchema),
    values: isCreateMode
      ? {
          name: '',
          description: '',
          posthogFlagKey: '',
          timezone: '',
          startTime: undefined,
          endTime: undefined,
          rank: 0,
        }
      : {
          name: experiment?.name ?? '',
          description: experiment?.description ?? '',
          posthogFlagKey: experiment?.posthogFlagKey ?? '',
          timezone: experiment?.timezone ?? '',
          startTime: parseDate(experiment?.startTime),
          endTime: parseDate(experiment?.endTime),
          rank: experiment?.rank ?? 0,
        },
    mode: 'onChange',
  });

  const [createExperiment] = useMutation(createExperimentMutation, {
    refetchQueries: [{ query: getExperimentsQuery }],
    onCompleted: (data) => {
      const newId = (data?.insertExperimentsOne as { id: string })?.id;
      if (newId) {
        navigate(`/admin/experiments/${newId}`, { replace: true });
      }
    },
    onError: () => {
      toast.error('Failed to create experiment');
    },
  });

  const [updateExperiment] = useMutation(updateExperimentMutation, {
    onCompleted: () => {
      toast.success('Experiment updated');
      form.reset(form.getValues());
    },
    onError: () => {
      toast.error('Failed to update experiment');
    },
  });

  const onSubmit = (data: ExperimentFormValues) => {
    if (isCreateMode || !experimentId) {
      createExperiment({
        variables: {
          name: data.name,
          description: data.description || null,
          posthogFlagKey: data.posthogFlagKey,
          timezone: data.timezone || null,
          startTime: data.startTime?.toISOString() ?? null,
          endTime: data.endTime?.toISOString() ?? null,
          rank: data.rank,
        },
      });
    } else {
      updateExperiment({
        variables: {
          experimentId: experimentId,
          name: data.name,
          description: data.description || null,
          posthogFlagKey: isActivated ? undefined : data.posthogFlagKey,
          timezone: data.timezone || null,
          startTime: data.startTime?.toISOString() ?? null,
          endTime: data.endTime?.toISOString() ?? null,
          rank: data.rank,
        },
      });
    }
  };

  const handleCancel = () => {
    if (isCreateMode) {
      navigate('/admin/experiments');
    } else {
      form.reset();
    }
  };

  const handleFlagKeyBlur = async () => {
    const flagKey = form.getValues('posthogFlagKey');
    if (!flagKey || !isCreateMode) return;

    setFlagValidation({ isValidating: true, exists: null });

    try {
      const response = await apiClient.experiments.syncFromPostHog({
        body: { posthogFlagKey: flagKey },
      });

      if (response.status === 200) {
        setFlagValidation({ isValidating: false, exists: true });
      } else if (response.status === 404) {
        setFlagValidation({ isValidating: false, exists: false });
      } else {
        setFlagValidation({ isValidating: false, exists: null });
      }
    } catch {
      setFlagValidation({ isValidating: false, exists: null });
    }
  };

  return (
    <VStack gap={6}>
      <HStack gap={3} align="center">
        <HStack gap={3} align="center">
          <PageTitle>
            {isCreateMode ? 'New Experiment' : experiment?.name}
          </PageTitle>
          {!isCreateMode && experiment && (
            <>
              <Badge variant={experiment.isActive ? 'success' : 'secondary'}>
                {experiment.isActive ? 'Active' : 'Inactive'}
              </Badge>
              {isActivated && <Badge variant="info">Locked</Badge>}
            </>
          )}
        </HStack>
        {!isCreateMode && experiment && (
          <HStack gap={2} wFull={false}>
            <TestAssignmentSection experimentId={experiment.id} />
            <ExperimentStatusToggle experimentId={experiment.id} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <NewButton type="outline" icon={{ name: 'MoreHorizontal' }} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DeleteExperimentAction
                  experimentId={experiment.id}
                  experimentName={experiment.name}
                  isActivated={isActivated ?? false}
                  asMenuItem
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </HStack>
        )}
      </HStack>

      {!isCreateMode && experiment && (
        <div className="w-full rounded-lg border border-border-primary bg-bg-gray-subtle p-4">
          <VStack gap={4}>
            <div className="grid w-full grid-cols-2 gap-4 md:grid-cols-4">
              <VStack gap={1}>
                <Text size="sm" color="tertiary">
                  First Activated
                </Text>
                <Text weight="medium">
                  {experiment.firstActivatedAt
                    ? formatDate.toLocalDateTime(experiment.firstActivatedAt)
                    : '-'}
                </Text>
              </VStack>
              <VStack gap={1}>
                <Text size="sm" color="tertiary">
                  Last Updated
                </Text>
                <Text weight="medium">
                  {formatDate.toLocalDateTime(experiment.updatedAt)}
                </Text>
              </VStack>
              <VStack gap={1}>
                <Text size="sm" color="tertiary">
                  Created At
                </Text>
                <Text weight="medium">
                  {formatDate.toLocalDateTime(experiment.createdAt)}
                </Text>
              </VStack>
              <VStack gap={1}>
                <Text size="sm" color="tertiary">
                  Variants
                </Text>
                <Text weight="medium">{experiment.variants?.length ?? 0}</Text>
              </VStack>
            </div>
          </VStack>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
          <VStack gap={6}>
            <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field, fieldState: { error } }) => (
                  <FormItem className="w-full">
                    <Label className="w-full">
                      <VStack>
                        Name *
                        <Input
                          placeholder="Experiment name"
                          className="w-full"
                          {...field}
                        />
                        {error && <FormMessage>{error.message}</FormMessage>}
                      </VStack>
                    </Label>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="posthogFlagKey"
                render={({ field, fieldState: { error } }) => (
                  <FormItem className="w-full">
                    <Label className="w-full">
                      <VStack>
                        PostHog Flag Key *
                        <HStack gap={2} align="center">
                          <Input
                            placeholder="posthog-flag-key"
                            className="w-full"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            {...field}
                            disabled={isActivated ?? false}
                            onBlur={() => {
                              field.onBlur();
                              handleFlagKeyBlur();
                            }}
                          />
                          {isCreateMode && flagValidation.isValidating && (
                            <Loader2 className="size-5 animate-spin text-text-tertiary" />
                          )}
                          {isCreateMode && flagValidation.exists === true && (
                            <CheckCircle className="size-5 text-text-positive" />
                          )}
                        </HStack>
                        {error && <FormMessage>{error.message}</FormMessage>}
                        {isCreateMode && flagValidation.exists === false && (
                          <Text size="sm" color="tertiary">
                            Feature flag doesn't exist in PostHog, ensure it is
                            created
                          </Text>
                        )}
                        {isActivated && (
                          <Text size="sm" color="tertiary">
                            Cannot change after activation
                          </Text>
                        )}
                      </VStack>
                    </Label>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="w-full">
                  <Label className="w-full">
                    <VStack>
                      Description
                      <Textarea
                        placeholder="Optional description of the experiment"
                        className="w-full"
                        rows={3}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </VStack>
                  </Label>
                </FormItem>
              )}
            />

            <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-4">
              <FormField
                control={form.control}
                name="timezone"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <Label className="w-full">
                      <VStack>
                        Timezone
                        <Select
                          onValueChange={field.onChange}
                          value={field.value ?? ''}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select timezone" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIMEZONES.map((tz) => (
                              <SelectItem key={tz.value} value={tz.value}>
                                {tz.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </VStack>
                    </Label>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <InputLabel label="Start Time">
                      <DateTimePicker
                        dateTime={field.value}
                        onDateTimeChange={field.onChange}
                        placeholder="Select start time"
                      />
                    </InputLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <InputLabel label="End Time">
                      <DateTimePicker
                        dateTime={field.value}
                        onDateTimeChange={field.onChange}
                        placeholder="Select end time"
                      />
                    </InputLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="rank"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <Label className="w-full">
                      <VStack>
                        Rank
                        <Input
                          type="number"
                          placeholder="0"
                          className="w-full"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                        <Text size="sm" color="tertiary">
                          Higher = higher priority
                        </Text>
                      </VStack>
                    </Label>
                  </FormItem>
                )}
              />
            </div>

            <HStack justify="end" gap={2}>
              <NewButton
                type="outline"
                onClick={handleCancel}
                text={isCreateMode ? 'Cancel' : 'Cancel Edit'}
                disabled={!form.formState.isDirty}
              />
              <NewButton
                submit
                disabled={
                  form.formState.isSubmitting ||
                  !form.formState.isValid ||
                  !form.formState.isDirty ||
                  (isCreateMode && flagValidation.exists !== true)
                }
                text={isCreateMode ? 'Create Experiment' : 'Save Changes'}
              />
            </HStack>
          </VStack>
        </form>
      </Form>

      {!isCreateMode && experiment && (
        <>
          <Divider />
          <ExperimentVariantsList
            experimentId={experiment.id}
            variants={experiment.variants ?? []}
            isActivated={isActivated ?? false}
            posthogFlagKey={experiment.posthogFlagKey ?? ''}
          />
        </>
      )}

      {!isCreateMode && experiment && (
        <>
          <Divider />
          <TestingOverridesSection
            experimentId={experiment.id}
            variants={experiment.variants ?? []}
            testingOverrides={
              (experiment.testingOverrides ?? []) as TestingOverride[]
            }
          />
        </>
      )}
    </VStack>
  );
};
